import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { usePublicSettings } from "@/hooks/usePublicSettings";

export default function Login() {
  const branding = usePublicSettings();
  const brand = branding?.clinic_name || "DentArt";
  useEffect(() => { if (brand) document.title = `${brand} · Hyrje`; }, [brand]);
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim(), password);
    setSubmitting(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2 bg-[#FDFBF7]" data-testid="login-page">
      {/* Left visual panel */}
      <div className="relative hidden lg:flex overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/9007c6ee-07d2-422a-9be8-65d4084cb76a/images/682bcefca9ff925a10cfcb6420e408e5805836a4050e6521fd4c8398b0c5e9c2.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2C4C3B]/70 via-[#2C4C3B]/20 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-[#FDFBF7]">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FDFBF7] text-[#2C4C3B]">
              <Sparkles size={20} />
            </div>
            <span className="text-lg font-semibold tracking-tight" data-testid="login-brand-name">{brand}</span>
          </div>
          <div className="max-w-md">
            <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">
              Mjekësi dentare moderne, e menaxhuar me kujdes.
            </h2>
            <p className="mt-4 text-sm text-[#FDFBF7]/85 leading-relaxed">
              Platforma e plotë për klinikat dentare në gjuhën shqipe — pacientë, takime, odontogramë dhe faturim, të gjitha në një vend.
            </p>
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#FDFBF7]/70">
            v1.0 · Sistemi i klinikës
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10 lg:hidden flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2C4C3B] text-[#FDFBF7]">
              <Sparkles size={20} />
            </div>
            <span className="text-lg font-semibold tracking-tight">{brand}</span>
          </div>
          <div className="label-eyebrow mb-3">Hyrje në Sistem</div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Mirë se vini sërish.</h1>
          <p className="mt-2 text-sm text-[#5C5C5C]">Vendosni kredencialet për të vazhduar.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="emri@klinika.al"
                required
                data-testid="login-email-input"
                className="mt-2 h-12 rounded-lg border-[#E2DFD8] bg-white"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-eyebrow">Fjalëkalimi</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password-input"
                className="mt-2 h-12 rounded-lg border-[#E2DFD8] bg-white"
              />
            </div>

            {error && (
              <div
                className="rounded-lg border border-[#9E3C3C]/30 bg-[#9E3C3C]/10 px-4 py-3 text-sm text-[#9E3C3C]"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full h-12"
              data-testid="login-submit-button"
            >
              {submitting ? "Duke hyrë…" : "Hyr në sistem"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <a href="/portal" className="text-[#2C4C3B] hover:text-[#3D6A52] font-medium" data-testid="public-portal-link">
              Jeni pacient? → Rezervoni online
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
